import { onSet } from 'nanostores';

import type { createCompareStore } from './sub_stores/compare.js';
import type { createPlayableMediaStore } from './sub_stores/playable_media.js';
import type { createPlaylistStore } from './sub_stores/playlist.js';
import type { createSegmentStore } from './sub_stores/segment.js';
import type { createSyncStore } from './sub_stores/sync.js';
import type { createTransformationsStore } from './sub_stores/transformations.js';

export function setupGlobalStoreListeners<PlaylistItemMeta>({
  playlist,
  segment,
  transformations,
  compare,
  playableMedia,
  sync,
  clampTime,
  handleLooping,
}: {
  playlist: ReturnType<typeof createPlaylistStore<PlaylistItemMeta>>;
  segment: ReturnType<typeof createSegmentStore<PlaylistItemMeta>>;
  transformations: ReturnType<typeof createTransformationsStore>;
  compare: ReturnType<typeof createCompareStore<PlaylistItemMeta>>;
  playableMedia: ReturnType<typeof createPlayableMediaStore<PlaylistItemMeta>>;
  sync: ReturnType<typeof createSyncStore>;
  clampTime: (time: number) => number;
  clampPlayableTime: (time: number) => number;
  handleLooping: () => void;
}) {
  // Current media has changed so need to reset some things.
  //
  // Important not to reset drawing annotations or current segment here
  // because these parts should be handled by the application level:
  // - Auto annotations (personal drafts) posted on navigation.
  // - Segments loading might happen before the playlist item is ready.
  // In these scenarios tracking playlist item change and resetting described state here
  // would interfere with this workflow and break them or make unstable.
  playlist.$currentPlaylistItem.listen((playlistItem, oldPlaylistItem) => {
    if (oldPlaylistItem?.id === playlistItem?.id) {
      return;
    }
    const userInSync =
      sync.$userSyncMode.get() === 'SYNC' ||
      sync.$userSyncMode.get() === 'PRESENTATION';

    transformations.setZoom(1);
    transformations.setPan({ x: 0.5, y: 0.5 });
    compare.setCompareItemId(undefined);
    compare.setCompareActive(false);
    playableMedia.setPlaybackRate(1);
    // Set duration from playlist item if available, otherwise undefined until the
    // media element's durationchange event provides the actual duration
    playableMedia.setDuration(playlistItem?.duration ?? 0);

    if (!userInSync) {
      playableMedia.setShouldBePlaying(false);
      playableMedia.$targetTime.set(undefined);
    }

    transformations.setFlip(false);
    transformations.setFlop(false);
    transformations.setRotation(0);
    playableMedia.setLastFoundFrameTime(undefined);
    segment.clearDismissedSegments();
    playableMedia.resetAudioMeter();

    playableMedia.setUnlockMediaPasswordStatus(undefined);
  });

  playableMedia.$isLive.listen((isLive) => {
    if (isLive) {
      compare.setCompareActive(false);
    }
  });

  function stopOrLoop() {
    const shouldBePlaying = playableMedia.$shouldBePlaying.get();
    const stepping = playableMedia.$stepping.get();
    const shouldLoop = playableMedia.$shouldLoop.get();
    if ((shouldBePlaying || stepping) && shouldLoop) {
      handleLooping();
      return;
    }
    playableMedia.pause();
  }

  onSet(playableMedia.$currentTime, ({ newValue, abort }) => {
    if (newValue === undefined) {
      return;
    }
    const clampedTime = clampTime(newValue);
    const playbackTrajectory = playableMedia.$playbackTrajectory.get();
    const stepping = playableMedia.$stepping.get();
    if (stepping) {
      setTimeout(() => {
        playableMedia.setStepping(false);
      }, 0);
    }
    if (clampedTime !== newValue) {
      // A live stream's edge trails the media element, so playing past it is
      // normal and must not read as reaching the end of the media
      if (playableMedia.$isLive.get() && !segment.$currentSegment.get().range) {
        return;
      }
      abort();
      playableMedia.$currentTime.set(clampedTime);
      const frameTolerance = playlist.$frameDuration.get() / 3;
      // Add frametolerance to not loop when stepping to the last frame
      if (
        clampedTime + frameTolerance < newValue &&
        (playbackTrajectory === 'forward' ||
          playbackTrajectory === 'forward_slow')
      ) {
        stopOrLoop();
      }

      if (
        clampedTime > newValue + frameTolerance &&
        (playbackTrajectory === 'backward' ||
          playbackTrajectory === 'backward_slow' ||
          stepping)
      ) {
        stopOrLoop();
      }
    }
  });
}
